import { useState, useCallback, useEffect, useRef } from 'react';

/**
 * useAutoHide - 2025 Auto-Hide UI Hook
 *
 * Automatically hides UI elements after a period of inactivity,
 * similar to iOS FaceTime, YouTube, and Netflix video players.
 *
 * Features:
 * - Configurable hide delay
 * - Touch/mouse interaction tracking
 * - Manual show/hide control
 * - Activity-based visibility extension
 * - Callback support for animations
 */
const useAutoHide = (options = {}) => {
  const {
    // Default hide delay in ms (3 seconds like iOS FaceTime)
    delay = 3000,
    // Start visible or hidden
    initialVisible = true,
    // Callback when visibility changes
    onVisibilityChange = null,
    // Keep visible while specific condition is true
    keepVisibleWhile = false,
    // Disable auto-hide entirely
    disabled = false,
  } = options;

  const [isVisible, setIsVisible] = useState(initialVisible);
  const hideTimeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  /**
   * Clear any existing timeout
   */
  const clearHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  /**
   * Schedule hiding after delay
   */
  const scheduleHide = useCallback(() => {
    if (disabled || keepVisibleWhile) return;

    clearHideTimeout();
    hideTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
      onVisibilityChange?.(false);
    }, delay);
  }, [delay, disabled, keepVisibleWhile, clearHideTimeout, onVisibilityChange]);

  /**
   * Show UI and restart hide timer
   */
  const show = useCallback(() => {
    lastActivityRef.current = Date.now();
    setIsVisible(true);
    onVisibilityChange?.(true);
    scheduleHide();
  }, [scheduleHide, onVisibilityChange]);

  /**
   * Hide UI immediately
   */
  const hide = useCallback(() => {
    clearHideTimeout();
    setIsVisible(false);
    onVisibilityChange?.(false);
  }, [clearHideTimeout, onVisibilityChange]);

  /**
   * Toggle visibility
   */
  const toggle = useCallback(() => {
    if (isVisible) {
      hide();
    } else {
      show();
    }
  }, [isVisible, show, hide]);

  /**
   * Extend visibility (restart timer without showing)
   * Use when user is actively engaged but not directly interacting
   */
  const extend = useCallback(() => {
    if (isVisible) {
      scheduleHide();
    }
  }, [isVisible, scheduleHide]);

  /**
   * Register user activity (touch, mouse move, etc.)
   * Shows UI if hidden, extends timer if visible
   */
  const registerActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    if (!isVisible) {
      show();
    } else {
      scheduleHide();
    }
  }, [isVisible, show, scheduleHide]);

  /**
   * Get time since last activity
   */
  const getTimeSinceActivity = useCallback(() => {
    return Date.now() - lastActivityRef.current;
  }, []);

  /**
   * Props to spread on container element for automatic interaction tracking
   */
  const containerProps = {
    onClick: registerActivity,
    onTouchStart: registerActivity,
    onMouseMove: registerActivity,
  };

  // Handle keepVisibleWhile changes
  useEffect(() => {
    if (keepVisibleWhile) {
      clearHideTimeout();
      setIsVisible(true);
    } else if (isVisible && !disabled) {
      scheduleHide();
    }
  }, [keepVisibleWhile, isVisible, disabled, clearHideTimeout, scheduleHide]);

  // Handle disabled changes
  useEffect(() => {
    if (disabled) {
      clearHideTimeout();
      setIsVisible(true);
    }
  }, [disabled, clearHideTimeout]);

  // Initial hide schedule
  useEffect(() => {
    if (initialVisible && !disabled && !keepVisibleWhile) {
      scheduleHide();
    }

    return () => {
      clearHideTimeout();
    };
  }, []); // Only on mount

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearHideTimeout();
    };
  }, [clearHideTimeout]);

  return {
    // State
    isVisible,

    // Actions
    show,
    hide,
    toggle,
    extend,
    registerActivity,

    // Utilities
    getTimeSinceActivity,
    containerProps,
  };
};

/**
 * Preset configurations for common use cases
 */
export const autoHidePresets = {
  // iOS FaceTime style - 3 second hide
  facetime: {
    delay: 3000,
    initialVisible: true,
  },

  // YouTube player controls - 3 seconds
  videoPlayer: {
    delay: 3000,
    initialVisible: true,
  },

  // Quick tooltip - 2 seconds
  tooltip: {
    delay: 2000,
    initialVisible: true,
  },

  // Notification toast - 5 seconds
  toast: {
    delay: 5000,
    initialVisible: true,
  },

  // Study code display - 30 seconds (important info)
  studyCode: {
    delay: 30000,
    initialVisible: true,
  },

  // Controls during screen share - 4 seconds
  screenShare: {
    delay: 4000,
    initialVisible: true,
  },
};

export default useAutoHide;
